import { user } from 'utils/auth/user';
import { sendApiRequest } from '.';
import { ISessionData } from 'data/schema';

interface IGetScheduledSessionsParams {
  /** ISO 8601 date string - start of date range */
  startDate?: string;
  /** ISO 8601 date string - end of date range */
  endDate?: string;
  /** Max number of results to return */
  limit?: number;
  /** Only return future sessions (default: true if no date range) */
  futureOnly?: boolean;
}

/**
 * Get scheduled sessions for the authenticated dealer
 *
 * @param params - Optional query parameters for filtering
 * @returns Array of session data
 *
 * @example
 * // Get all future sessions (default)
 * const sessions = await getScheduledSessions();
 *
 * @example
 * // Get sessions in a specific month
 * const sessions = await getScheduledSessions({
 *   startDate: '2026-01-01T00:00:00Z',
 *   endDate: '2026-01-31T23:59:59Z'
 * });
 *
 * @example
 * // Get next 10 upcoming sessions
 * const sessions = await getScheduledSessions({
 *   futureOnly: true,
 *   limit: 10
 * });
 */
export async function getScheduledSessions(params?: IGetScheduledSessionsParams): Promise<ISessionData[]> {
  // Build query string from params
  const queryParams = new URLSearchParams();

  if (params?.startDate) {
    queryParams.append('startDate', params.startDate);
  }
  if (params?.endDate) {
    queryParams.append('endDate', params.endDate);
  }
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params?.futureOnly !== undefined) {
    queryParams.append('futureOnly', params.futureOnly.toString());
  }

  const queryString = queryParams.toString();
  const url = queryString ? `/schedule?${queryString}` : '/schedule';

  const response = await sendApiRequest<ISessionData[]>(
    url,
    {
      method: 'GET',
      headers: {
        'X-User': user.userPseudonym,
        'X-Dealer': user.dealerId,
      },
    },
    true
  );

  return response || [];
}

/**
 * Create a new scheduled session
 */
export async function createScheduledSession(data: Omit<ISessionData, 'id' | 'dealerId'>): Promise<ISessionData> {
  const result = await sendApiRequest<ISessionData>(
    '/schedule',
    {
      method: 'POST',
      body: data,
      headers: {
        'X-User': user.userPseudonym,
        'X-Dealer': user.dealerId,
      },
    },
    true
  );
  if (!result) {
    throw new Error('Failed to create scheduled session');
  }
  return result;
}

/**
 * Update a scheduled session
 */
export async function updateScheduledSession(
  sessionId: string,
  data: Partial<Omit<ISessionData, 'id' | 'dealerId'>>
): Promise<ISessionData> {
  const result = await sendApiRequest<ISessionData>(
    `/schedule/${sessionId}`,
    {
      method: 'PUT',
      body: data,
      headers: {
        'X-User': user.userPseudonym,
        'X-Dealer': user.dealerId,
      },
    },
    true
  );
  if (!result) {
    throw new Error('Failed to update scheduled session');
  }
  return result;
}

/**
 * Delete a scheduled session
 */
export async function deleteScheduledSession(sessionId: string): Promise<void> {
  await sendApiRequest(
    `/schedule/${sessionId}`,
    {
      method: 'DELETE',
      headers: {
        'X-User': user.userPseudonym,
        'X-Dealer': user.dealerId,
      },
    },
    true
  );
}
